"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import {
  isActiveThumbnailPhase,
  type ThumbnailJobPhase,
} from "@/lib/thumbnail-jobs";

export type ThumbnailTrayItem = {
  videoId: string;
  title: string;
  phase: ThumbnailJobPhase;
  loaded: number;
  total: number;
  error?: string;
};

type StoredJob = {
  videoId: string;
  title: string;
};

type ThumbnailJobContextValue = {
  jobs: ThumbnailTrayItem[];
  enqueueDefaultThumbnail: (videoId: string, title: string) => void;
  clearCompleted: () => void;
};

type JobStatusResponse = {
  videoId: string;
  title: string;
  phase: ThumbnailJobPhase | "idle";
  loaded: number;
  total: number;
  error?: string;
  hasThumbnail: boolean;
};

const ThumbnailJobContext = createContext<ThumbnailJobContextValue | null>(
  null,
);

export function useThumbnailJobs() {
  const ctx = useContext(ThumbnailJobContext);
  if (!ctx) {
    throw new Error("useThumbnailJobs must be used within ThumbnailJobProvider");
  }
  return ctx;
}

function storageKey(userId: string): string {
  return `gv-thumb-jobs:${userId}`;
}

function loadStored(userId: string): StoredJob[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (
        !item ||
        typeof item !== "object" ||
        typeof (item as StoredJob).videoId !== "string" ||
        typeof (item as StoredJob).title !== "string"
      ) {
        return [];
      }
      const { videoId, title } = item as StoredJob;
      if (!videoId.trim()) return [];
      return [{ videoId, title }];
    });
  } catch {
    return [];
  }
}

function saveStored(userId: string, jobs: ThumbnailTrayItem[]): void {
  const persist: StoredJob[] = jobs
    .filter((job) => job.phase !== "done")
    .map((job) => ({ videoId: job.videoId, title: job.title }));
  try {
    if (persist.length === 0) {
      localStorage.removeItem(storageKey(userId));
    } else {
      localStorage.setItem(storageKey(userId), JSON.stringify(persist));
    }
  } catch {
    // Ignore quota / private-mode failures.
  }
}

function toTrayItem(
  status: JobStatusResponse,
  fallbackTitle: string,
): ThumbnailTrayItem {
  return {
    videoId: status.videoId,
    title: status.title || fallbackTitle,
    phase: status.phase === "idle" ? "queued" : status.phase,
    loaded: status.loaded,
    total: status.total,
    error: status.error,
  };
}

export function ThumbnailJobProvider({
  children,
  userId,
}: {
  children: ReactNode;
  userId?: string | null;
}) {
  const router = useRouter();
  const [jobs, setJobs] = useState<ThumbnailTrayItem[]>([]);
  const [restoredFor, setRestoredFor] = useState<string | null>(null);
  const restoredUserId = useRef<string | null>(null);
  const starting = useRef(new Set<string>());
  const refreshedDone = useRef(new Set<string>());

  const startOnServer = useCallback(async (videoId: string, title: string) => {
    if (starting.current.has(videoId)) return;
    starting.current.add(videoId);
    try {
      const res = await fetch(`/api/videos/${videoId}/default-thumbnail`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null;
        setJobs((prev) =>
          prev.map((job) =>
            job.videoId === videoId
              ? {
                  ...job,
                  phase: "error",
                  error: body?.error ?? "Could not start thumbnail job.",
                }
              : job,
          ),
        );
        return;
      }
      const data = (await res.json()) as { job?: JobStatusResponse };
      if (!data.job) return;
      setJobs((prev) =>
        prev.map((job) =>
          job.videoId === videoId ? toTrayItem(data.job!, title) : job,
        ),
      );
    } catch {
      setJobs((prev) =>
        prev.map((job) =>
          job.videoId === videoId
            ? {
                ...job,
                phase: "error",
                error: "Could not start thumbnail job.",
              }
            : job,
        ),
      );
    } finally {
      starting.current.delete(videoId);
    }
  }, []);

  const applyStatuses = useCallback(
    (statuses: JobStatusResponse[], requestedIds: string[]) => {
      const requested = new Set(requestedIds);
      const byId = new Map(statuses.map((status) => [status.videoId, status]));
      const idle = statuses
        .filter((status) => status.phase === "idle")
        .map((status) => ({
          videoId: status.videoId,
          title: status.title,
        }));

      setJobs((prev) =>
        prev.map((job) => {
          if (!requested.has(job.videoId)) return job;
          const status = byId.get(job.videoId);
          if (!status) {
            return {
              ...job,
              phase: "error",
              error: "Video not found.",
            };
          }
          if (status.phase === "idle") {
            return {
              ...job,
              title: status.title || job.title,
              phase: "queued",
            };
          }
          return toTrayItem(status, job.title);
        }),
      );

      for (const item of idle) {
        void startOnServer(item.videoId, item.title);
      }
    },
    [startOnServer],
  );

  const poll = useCallback(
    async (ids: string[]) => {
      if (ids.length === 0) return;
      try {
        const res = await fetch(
          `/api/videos/thumbnail-jobs?ids=${encodeURIComponent(ids.join(","))}`,
        );
        if (!res.ok) return;
        const data = (await res.json()) as { jobs?: JobStatusResponse[] };
        applyStatuses(data.jobs ?? [], ids);
      } catch {
        // Keep showing the last known client state.
      }
    },
    [applyStatuses],
  );

  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      restoredUserId.current = null;
      void Promise.resolve().then(() => {
        if (cancelled) return;
        setJobs([]);
        setRestoredFor(null);
      });
      return () => {
        cancelled = true;
      };
    }
    if (restoredUserId.current === userId) return;
    restoredUserId.current = userId;
    const stored = loadStored(userId);
    void Promise.resolve().then(() => {
      if (cancelled) return;
      setJobs(
        stored.map((item) => ({
          videoId: item.videoId,
          title: item.title,
          phase: "queued" as const,
          loaded: 0,
          total: 0,
        })),
      );
      setRestoredFor(userId);
      if (stored.length > 0) {
        void poll(stored.map((item) => item.videoId));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, poll]);

  useEffect(() => {
    if (!userId || restoredFor !== userId) return;
    saveStored(userId, jobs);
  }, [jobs, userId, restoredFor]);

  const inflightKey = jobs
    .filter((job) => isActiveThumbnailPhase(job.phase))
    .map((job) => job.videoId)
    .join(",");

  useEffect(() => {
    if (!inflightKey) return;
    const ids = inflightKey.split(",");
    const interval = window.setInterval(() => {
      void poll(ids);
    }, 1000);
    return () => window.clearInterval(interval);
  }, [inflightKey, poll]);

  useEffect(() => {
    const newlyDone = jobs.filter(
      (job) => job.phase === "done" && !refreshedDone.current.has(job.videoId),
    );
    if (newlyDone.length === 0) return;
    for (const job of newlyDone) {
      refreshedDone.current.add(job.videoId);
    }
    router.refresh();
  }, [jobs, router]);

  const enqueueDefaultThumbnail = useCallback(
    (videoId: string, title: string) => {
      let shouldStart = true;
      setJobs((prev) => {
        const existing = prev.find((job) => job.videoId === videoId);
        if (existing && isActiveThumbnailPhase(existing.phase)) {
          shouldStart = false;
          return prev;
        }
        return [
          ...prev.filter((job) => job.videoId !== videoId),
          {
            videoId,
            title,
            phase: "queued",
            loaded: 0,
            total: 0,
          },
        ];
      });
      if (shouldStart) {
        void startOnServer(videoId, title);
      }
    },
    [startOnServer],
  );

  const clearCompleted = useCallback(() => {
    setJobs((prev) =>
      prev.filter((job) => job.phase !== "done" && job.phase !== "error"),
    );
  }, []);

  const value = useMemo(
    () => ({ jobs, enqueueDefaultThumbnail, clearCompleted }),
    [jobs, enqueueDefaultThumbnail, clearCompleted],
  );

  return (
    <ThumbnailJobContext.Provider value={value}>
      {children}
    </ThumbnailJobContext.Provider>
  );
}
